import { useEffect, useState, useMemo } from 'react';
import logger from 'ws-scripts/modules/logger';
import { debounce } from '../utils/debounce';

const LOG_PREFIX = '[ws-locations-control][useFetchLocationSuggestions]';
/**
 * Fetches Google Maps location suggestions based on user input.
 * This hook manages debouncing and session handling for autocomplete requests.
 *
 * @param {string} inputValue - The current text from the search input.
 * @param {object | null} sessionToken - The active AutocompleteSessionToken from the search session.
 * @param {boolean} isApiReady - A flag indicating if the Google Maps API script has loaded.
 */
export const useFetchLocationSuggestions = (
	inputValue,
	sessionToken,
	isApiReady
) => {
	const [suggestions, setSuggestions] = useState([]);

	const fetchSuggestions = useMemo(
		() =>
			debounce(async (input, token) => {
				try {
					const { AutocompleteSuggestion } =
						window.google.maps.places;
					const request = {
						input: input.trim(),
						language: 'en-US',
						includedPrimaryTypes: ['(cities)'],
						includedRegionCodes: ['us'],
						sessionToken: token
					};
					const { suggestions: fetched } =
						await AutocompleteSuggestion.fetchAutocompleteSuggestions(
							request
						);
					setSuggestions(fetched || []);
				} catch (error) {
					logger.error(
						`${LOG_PREFIX} Failed to fetch suggestions:`,
						error
					);
					setSuggestions([]);
				}
			}),
		[]
	);

	useEffect(() => {
		// No active session / API not ready
		if (!isApiReady || !sessionToken) {
			setSuggestions([]);
			return null;
		}

		fetchSuggestions.run(inputValue, sessionToken);

		return () => {
			fetchSuggestions.cancel();
		};
	}, [inputValue, isApiReady, sessionToken, fetchSuggestions]);

	return { suggestions };
};
